import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  aufgabenResponseSchema,
  bereichResponseSchema,
  type AufgabenResponse,
} from "@msc/shared";
import { useTranslation } from "react-i18next";

import { api } from "../lib/api.js";

export const AUFGABEN_KEY = ["dashboard", "aufgaben"] as const;
export const BEREICH_KEY = ["dashboard", "bereich"] as const;

/** Startseite: alles Offene der angemeldeten Person (`GET /dashboard/aufgaben`). */
export function useAufgaben() {
  return useQuery({
    queryKey: AUFGABEN_KEY,
    queryFn: async () => aufgabenResponseSchema.parse(await api.get<unknown>("/dashboard/aufgaben")),
    staleTime: 60_000,
  });
}

/** Bereichssicht für Führung. Nur laden, wenn der Reiter offen ist (`enabled`). */
export function useBereich(enabled: boolean) {
  return useQuery({
    queryKey: BEREICH_KEY,
    queryFn: async () => bereichResponseSchema.parse(await api.get<unknown>("/dashboard/bereich")),
    enabled,
    staleTime: 60_000,
    retry: false,
  });
}

/**
 * Weekly-Todo von der Startseite abhaken. Optimistisch: Der Punkt verschwindet
 * sofort aus der Liste und kommt nur zurück, wenn der Server ablehnt.
 */
export function useTodoErledigen() {
  const qc = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: (todoId: string) => api.post<void>(`/dashboard/aufgaben/todos/${todoId}/erledigen`),
    onMutate: async (todoId: string) => {
      await qc.cancelQueries({ queryKey: AUFGABEN_KEY });
      const vorher = qc.getQueryData<AufgabenResponse>(AUFGABEN_KEY);
      if (vorher) {
        qc.setQueryData<AufgabenResponse>(AUFGABEN_KEY, {
          ...vorher,
          items: vorher.items.filter((i) => i.todoId !== todoId),
        });
      }
      return { vorher };
    },
    onError: (_err, _todoId, ctx) => {
      if (ctx?.vorher) qc.setQueryData(AUFGABEN_KEY, ctx.vorher);
      notifications.show({ color: "red", message: t("aufgaben.erledigenFehlgeschlagen") });
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: AUFGABEN_KEY });
      void qc.invalidateQueries({ queryKey: BEREICH_KEY });
      void qc.invalidateQueries({ queryKey: ["weekly"] });
    },
  });
}
